import type { WhatsAppDestinationType, WhatsAppTemplateModel } from '../models/automation-api.models';
import { WHATSAPP_TEMPLATE_CATALOG } from './whatsapp-template.catalog';
import {
  isWhatsAppTemplateStaleOnApi,
  resolveExpectedWhatsAppVariableKeys,
} from './whatsapp-template-resolve.utils';
import { isWhatsAppVariableSlotValueValid } from './whatsapp-template-variables.util';

export function normalizeWhatsAppDestinationType(
  raw: unknown,
  legacyTargetPhone: string
): WhatsAppDestinationType {
  const s = typeof raw === 'string' ? raw.trim() : '';
  if (s === 'FixedPhone') return 'FixedNumber';
  if (
    s === 'FixedNumber' ||
    s === 'PersonField' ||
    s === 'CardAssignee' ||
    s === 'PhoneField' ||
    s === 'CardCreator'
  ) {
    return s;
  }
  return legacyTargetPhone.trim() ? 'FixedNumber' : 'FixedNumber';
}

export function isWhatsAppDestinationComplete(
  config: Record<string, unknown> | null | undefined
): boolean {
  const source = config ?? {};
  const templateKey = typeof source['templateKey'] === 'string' ? source['templateKey'].trim() : '';
  if (!templateKey) return false;

  const dest = normalizeWhatsAppDestinationType(
    source['destinationType'] ?? source['recipientType'],
    typeof source['targetPhone'] === 'string' ? source['targetPhone'] : ''
  );

  if (dest === 'FixedNumber') {
    const phone = typeof source['targetPhone'] === 'string' ? source['targetPhone'].trim() : '';
    return /^\+[1-9]\d{7,14}$/.test(phone);
  }
  if (dest === 'PersonField') {
    const id =
      (typeof source['personWorkflowFieldId'] === 'string' ? source['personWorkflowFieldId'].trim() : '')
      || (typeof source['recipientWorkflowFieldId'] === 'string' ? source['recipientWorkflowFieldId'].trim() : '');
    return !!id;
  }
  if (dest === 'PhoneField') {
    const id =
      (typeof source['phoneWorkflowFieldId'] === 'string' ? source['phoneWorkflowFieldId'].trim() : '')
      || (typeof source['recipientWorkflowFieldId'] === 'string' ? source['recipientWorkflowFieldId'].trim() : '');
    return !!id;
  }
  if (dest === 'CardAssignee' || dest === 'CardCreator') {
    return true;
  }
  return false;
}

export function hasWhatsAppVariablesFilled(
  config: Record<string, unknown> | null | undefined,
  templates?: readonly WhatsAppTemplateModel[] | null
): boolean {
  const source = config ?? {};
  const templateKey = typeof source['templateKey'] === 'string' ? source['templateKey'].trim() : '';
  if (!templateKey) return false;
  if (Array.isArray(templates) && templates.length === 0) {
    const inCatalog = WHATSAPP_TEMPLATE_CATALOG.some((item) => item.key === templateKey);
    if (!inCatalog) return false;
  }
  if (isWhatsAppTemplateStaleOnApi(templateKey, templates)) return false;
  const fromApi =
    source['variables'] && typeof source['variables'] === 'object'
      ? (source['variables'] as Record<string, unknown>)
      : {};
  const dest = normalizeWhatsAppDestinationType(
    source['destinationType'] ?? source['recipientType'],
    typeof source['targetPhone'] === 'string' ? source['targetPhone'] : ''
  );
  const keys = resolveExpectedWhatsAppVariableKeys(templateKey, templates);
  if (!keys.length) return true;
  return keys.every((key) => {
    if (!Object.prototype.hasOwnProperty.call(fromApi, key)) return false;
    const raw = fromApi[key];
    if (typeof raw !== 'string') return false;
    return isWhatsAppVariableSlotValueValid(key, raw, dest);
  });
}

export function isWhatsAppActionConfigReady(
  config: Record<string, unknown> | null | undefined,
  templates?: readonly WhatsAppTemplateModel[] | null
): boolean {
  return isWhatsAppDestinationComplete(config) && hasWhatsAppVariablesFilled(config, templates);
}
